import { useEffect, useRef } from 'react'
import type { UkmReceipt, UkmReceiptStats, UkmReceiptSyncMeta } from './receiptApi'

export type CashLiveEvent =
  | { type: 'ping' }
  | { type: 'receipt'; receipt: UkmReceipt }
  | { type: 'stats'; stats: UkmReceiptStats }
  | { type: 'sync'; sync: UkmReceiptSyncMeta | null }

type Handlers = {
  onEvent?: (ev: CashLiveEvent) => void
  onError?: () => void
  onOpen?: () => void
}

/**
 * SSE подписка на live-ленту чеков терминала.
 * Same-origin cookies (session) — как у REST api().
 */
export function useCashLive(
  terminalId: number | null | undefined,
  enabled: boolean,
  handlers: Handlers,
) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!enabled || !terminalId) return

    let es: EventSource | null = null
    let closed = false
    let fallbackTimer: ReturnType<typeof setInterval> | null = null

    const clearFallback = () => {
      if (fallbackTimer) {
        clearInterval(fallbackTimer)
        fallbackTimer = null
      }
    }

    const startFallback = () => {
      if (fallbackTimer) return
      fallbackTimer = setInterval(() => {
        handlersRef.current.onError?.()
      }, 15000)
    }

    const connect = () => {
      if (closed) return
      es = new EventSource(`/api/terminals/${terminalId}/receipts/live`, {
        withCredentials: true,
      })

      es.addEventListener('open', () => {
        clearFallback()
        handlersRef.current.onOpen?.()
      })

      es.addEventListener('cash', (msg) => {
        try {
          const data = JSON.parse((msg as MessageEvent).data) as CashLiveEvent
          if (data?.type === 'ping') return
          handlersRef.current.onEvent?.(data)
        } catch {
          /* ignore bad payload */
        }
      })

      es.onerror = () => {
        // Браузер сам переподключает EventSource; REST fallback — только пока канал мёртв
        startFallback()
      }
    }

    connect()

    return () => {
      closed = true
      clearFallback()
      try {
        es?.close()
      } catch {
        /* ignore */
      }
    }
  }, [terminalId, enabled])
}
